import React from 'react';
import { Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { isVirtualPortfolioEnabled } from '../constants/featureFlags';
import {
  TRANSACTIONS_ENABLED,
  VIRTUAL_PORTFOLIO_ENABLED,
} from '../config/featureFlags';
import PortfolioPage from '../PortfolioPage';
import VPortfolioPage from '../pages/VPortfolioPage';
import WalletsPage from '../pages/WalletsPage';
import WalletTransactionsPage from '../pages/WalletTransactionsPage';
import ManualHoldingsPage from '../pages/ManualHoldingsPage';
import SettingsPage from '../SettingsPage';
import CoinPage from '../pages/CoinPage';
import ExitStrategyPage from '../pages/ExitStrategyPage';
import AllExitStrategiesPage from '../pages/AllExitStrategiesPage';
import AdminPage from '../pages/AdminPage';
import AboutPage from '../pages/AboutPage';
import AddHoldingsPage from '../pages/AddHoldingsPage';
import LegalPage from '../pages/LegalPage';
import Login from './Login';
import HreflangTags from './HreflangTags';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser } = useAuth();
  return currentUser ? children : <Navigate to="/login" replace />;
};

const supportedLanguages = ['en', 'es', 'zh', 'hi', 'ar', 'pt', 'ru', 'ja', 'he', 'de', 'it', 'fr'];

// Component to handle language detection and redirect
function LanguageRedirect() {
  const { i18n } = useTranslation();
  const location = useLocation();
  
  // Get the current language or default to 'en'
  const currentLang = i18n.language || 'en';
  const savedLang = localStorage.getItem('preferredLanguage');
  const browserLang = navigator.language.split('-')[0];
  
  // Determine which language to use
  let targetLang = 'en'; // default
  
  if (savedLang && supportedLanguages.includes(savedLang)) {
    targetLang = savedLang;
  } else if (supportedLanguages.includes(browserLang)) {
    targetLang = browserLang;
  } else if (supportedLanguages.includes(currentLang)) {
    targetLang = currentLang;
  }
  
  // Redirect to language-specific URL
  const path = location.pathname === '/' ? '' : location.pathname;
  const search = location.search;
  const hash = location.hash;
  
  return <Navigate to={`/${targetLang}${path}${search}${hash}`} replace />;
}

// Component to handle language-specific routes
function LanguageRoutes() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();
  
  // Change i18n language if needed
  React.useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
      localStorage.setItem('preferredLanguage', lang);
    }
  }, [lang, i18n]);
  
  // Validate language parameter
  if (!supportedLanguages.includes(lang)) {
    return <Navigate to="/en" replace />;
  }
  
  return (
    <>
      <HreflangTags />
      <Routes>
        {/* Language-specific routes */}
        <Route path="/" element={<ProtectedRoute><PortfolioPage key={location.pathname} /></ProtectedRoute>} />
        {(VIRTUAL_PORTFOLIO_ENABLED && isVirtualPortfolioEnabled()) && (
          <Route path="/v-portfolio" element={<ProtectedRoute><VPortfolioPage /></ProtectedRoute>} />
        )}
        <Route path="/portfolio" element={<ProtectedRoute><PortfolioPage /></ProtectedRoute>} />
        <Route path="/wallets" element={<ProtectedRoute><WalletsPage /></ProtectedRoute>} />
        {TRANSACTIONS_ENABLED && (
          <Route path="/wallet-transactions" element={<ProtectedRoute><WalletTransactionsPage /></ProtectedRoute>} />
        )}
        <Route path="/manual-holdings" element={<ProtectedRoute><ManualHoldingsPage /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
        <Route path="/coin/:coinSymbol" element={<CoinPage />} />
        <Route path="/exit-strategy/:coinSymbol" element={<ProtectedRoute><ExitStrategyPage /></ProtectedRoute>} />
        <Route path="/all-exit-strategies" element={<ProtectedRoute><AllExitStrategiesPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/add-holdings" element={<ProtectedRoute><AddHoldingsPage /></ProtectedRoute>} />
        
        {/* Catch all - redirect to portfolio */}
        <Route path="*" element={<Navigate to={`/${lang}/portfolio`} replace />} />
      </Routes>
    </>
  );
}

// Main component that handles all routing
function LanguageAwareRoutes() {
  return (
    <Routes>
      {/* Root redirect to language detection */}
      <Route path="/" element={<LanguageRedirect />} />
      
      {/* Login page (no language prefix needed) */}
      <Route path="/login" element={<Login />} />
      
      {/* Language-specific routes */}
      <Route path="/:lang/*" element={<LanguageRoutes />} />
      
      {/* Catch all - redirect to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default LanguageAwareRoutes; 